import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Phone, Clock, MapPin, Award, Wrench, Home, Shield, Droplet, Zap, CheckCircle, Users, AlertTriangle, Gauge, Scissors, Camera, User, Building } from 'lucide-react';
import { SchemaInjector, createLocalBusinessSchema, createServiceSchema, createBreadcrumbSchema } from '../components/SchemaInjector';
import { createReviewSchema } from '../lib/schemas/aiSeoSchemas';
import { BreadcrumbNavigation, createBreadcrumbData } from '../components/BreadcrumbNavigation';

const DenverPlumber = () => {
  // Schema markup data for Denver
  const localBusinessSchema = createLocalBusinessSchema('Denver', {
    lat: '39.7392',
    lng: '-104.9903'
  });

  const waterHeaterServiceSchema = createServiceSchema(
    'Water Heater Installation - Denver',
    'Professional water heater installation and replacement in Denver, Colorado. Starting at $2,000 with transparent pricing.',
    2000
  );

  const sewerServiceSchema = createServiceSchema(
    'Sewer Camera Inspection - Denver',
    'Professional sewer camera scope inspection in Denver to diagnose issues before expensive repairs.',
    175
  );

  const breadcrumbSchema = createBreadcrumbSchema([
    { name: 'Home', url: 'https://colorado-plumbing.com/' },
    { name: 'Service Areas', url: 'https://colorado-plumbing.com/#service-area' },
    { name: 'Denver Plumber', url: 'https://colorado-plumbing.com/denver-plumber' }
  ]);

  // Enhanced Plumber Schema for E-E-A-T
  const plumberSchema = {
    "@context": "https://schema.org",
    "@type": "Plumber",
    "@id": "https://colorado-plumbing.com/",
    "name": "Colorado First Plumbing and Sewer LLC",
    "alternateName": "Colorado First Plumbing",
    "url": "https://colorado-plumbing.com",
    "logo": "https://colorado-plumbing.com/logo.png",
    "foundingDate": "2018",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "5108 Sydney Ave",
      "addressLocality": "Littleton",
      "addressRegion": "CO",
      "postalCode": "80130",
      "addressCountry": "US"
    },
    "areaServed": [
      { "@type": "City", "name": "Denver" },
      { "@type": "AdministrativeArea", "name": "Denver Metro" }
    ],
    "email": "codee@colorado-plumbing.com",
    "founders": [
      {
        "@type": "Person",
        "name": "Codee Jessen",
        "jobTitle": "Master Plumber"
      },
      {
        "@type": "Person",
        "name": "Michelle Jessen",
        "jobTitle": "Owner (Majority)"
      }
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "360",
      "bestRating": "5"
    },
    "hasCredential": [
      {
        "@type": "EducationalOccupationalCredential",
        "credentialCategory": "Master Plumber License",
        "recognizedBy": {
          "@type": "Organization",
          "name": "State of Colorado"
        }
      },
      {
        "@type": "EducationalOccupationalCredential",
        "credentialCategory": "BBB Accreditation",
        "recognizedBy": {
          "@type": "Organization",
          "name": "Better Business Bureau"
        },
        "dateCreated": "2024-07-15"
      }
    ]
  };

  // Comprehensive FAQPage Schema for AI Optimization
  const faqPageSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How much does a sewer repair in a Denver street cost?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC provides flat-rate pricing of $9,500 for sewer repairs requiring excavation into public streets in Denver. This all-inclusive price covers securing permits from Denver Public Works, managing traffic control, street cutting, excavation, pipe repair, and full restoration to city standards. No surprise fees or hidden costs."
        }
      },
      {
        "@type": "Question",
        "name": "What is the affordable price to replace an old sewer line in Denver?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing offers affordable full sewer line replacement in Denver for a flat rate of $12,000. The City and County of Denver typically requires traditional excavation where possible. Trenchless pipe bursting may be used when approved by Denver for special circumstances like detached garages or property constraints. Both methods use new seamless HDPE pipe rated for 50+ years with all permits and inspections included."
        }
      },
      {
        "@type": "Question",
        "name": "How much does a 50 gallon water heater cost in Denver?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "$2,100 fully installed (Summer Special price - Regular $2,525). This affordable all-inclusive price covers the Rheem or AO Smith 50-gallon unit, professional installation to Denver plumbing codes, thermal expansion tank, drain pan, new supply lines, old unit disposal, and city permit. No hidden fees."
        }
      },
      {
        "@type": "Question",
        "name": "What is the cost for tankless water heater replacement in Denver?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing provides affordable tankless water heater replacement in Denver for $5,000 to replace existing units or $8,000 for new installations converting from traditional tanks. Our flat-rate pricing includes Rinnai or Navien units, professional installation, venting modifications, and permits. Perfect for Denver's altitude and 40-degree groundwater."
        }
      },
      {
        "@type": "Question",
        "name": "How much does professional sewer camera inspection cost in Denver?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "$175 for comprehensive sewer camera inspection in Denver, including digital video recording and detailed written report. This affordable diagnostic service helps Denver homeowners identify problems early, preventing expensive repairs. Additional fees may apply for complex access like roof vents or toilet removal."
        }
      },
      {
        "@type": "Question",
        "name": "What does mainline snaking cost in older Denver homes without cleanouts?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "$695 flat rate for mainline snaking that includes pulling and resetting a toilet in older Denver homes lacking exterior cleanouts. Common in historic neighborhoods like Berkeley, Platt Park, and Congress Park. This all-inclusive price covers toilet removal, professional snaking, and proper reinstallation with new wax ring and bolts."
        }
      }
    ]
  };

  useEffect(() => {
    // SEO meta tags setup
    document.title = "Denver Plumber | Colorado First Plumbing | Affordable Prices Online";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Denver\'s ONLY affordable plumber showing actual prices online. Water heater $2,000, sewer scope $175. No hidden fees. 1000+ reviews across all platforms. Master plumber owned. Same-day service.');
    }

    // BirdEye widget loading
    const loadBirdEyeWidget = () => {
      const existingScript = document.querySelector('#birdeye-widget-script');
      if (existingScript) return;

      const script = document.createElement('script');
      script.id = 'birdeye-widget-script';
      script.type = 'text/javascript';
      script.src = 'https://birdeye.com/embed/v7/172384402926614/8/9876543218815183';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    };

    setTimeout(loadBirdEyeWidget, 1000);

    return () => {
      const script = document.querySelector('#birdeye-widget-script');
      if (script) script.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SchemaInjector 
        schemas={[localBusinessSchema, plumberSchema, waterHeaterServiceSchema, sewerServiceSchema, breadcrumbSchema, faqPageSchema]}
        title="Denver Plumber | Colorado First Plumbing | Affordable Prices Online"
        description="Denver's ONLY affordable plumber showing actual prices online. Water heater $2,000, sewer scope $175. No hidden fees. 1000+ reviews across all platforms. Master plumber owned."
        canonical="https://colorado-plumbing.com/denver-plumber"
        keywords="affordable plumber Denver, transparent pricing Denver, actual plumbing costs Denver, no hidden fees plumber Denver, 1000 reviews Denver"
      />
      <Header />
      <BreadcrumbNavigation 
        {...createBreadcrumbData.location('Denver')}
      />
      
      <main>
        {/* Hero Section - Castle Rock Style with Affordable Integration */}
        <section className="relative pt-48 pb-16 bg-gradient-to-br from-trust-primary via-authority-navy to-action-primary">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFFFFF_1px,transparent_1px)] [background-size:32px_32px]"></div>
          <div className="container-custom text-center relative z-10">
            {/* Trust Signals Banner */}
            <div className="inline-block px-6 py-3 mb-6 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
              <span className="text-white font-bold">🏆 Denver's Most Affordable Family-Owned Plumbers • Licensed Master Plumber • <Link to="/team/michelle-jessen" className="text-blue-200 hover:text-blue-100 underline">Woman Majority Owned</Link> • 1000+ Reviews Across All Platforms</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white leading-tight">
              Denver's ONLY Affordable Plumber with Exact Online Prices
              <span className="block text-2xl md:text-3xl mt-4 text-blue-200">
                Colorado First Plumbing and Sewer LLC
              </span>
            </h1>
            
            <p className="text-xl mb-8 text-blue-100 max-w-3xl mx-auto">
              How can others claim "affordable" without showing prices? We prove affordable means transparent - 
              see every exact price online before you call. No hidden fees, no salesmen, no surprises.
            </p>

            {/* Price Preview - Clickable Cards */}
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <Link to="/tankless-water-heater-pricing" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all duration-300">
                <span className="text-yellow-300 font-bold">Tankless Replace: $5,000</span>
              </Link>
              <Link to="/flat-rate-affordable-rheem-water-heaters-in-colorado" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all duration-300">
                <span className="text-yellow-300 font-bold">40 Gal Water Heater: $2,000</span>
              </Link>
              <Link to="/diagnostic-services-pricing" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all duration-300">
                <span className="text-yellow-300 font-bold">Sewer Scope: $175</span>
              </Link>
              <Link to="/sewer-replacement-spot-repair-pricing" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all duration-300">
                <span className="text-yellow-300 font-bold">Spot Repair: $5,000</span>
              </Link>
            </div>

            {/* Primary CTA */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              
              <a 
                href="/pricing" 
                className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-8 py-4 rounded-full text-xl font-semibold inline-block transition-all duration-300"
              >
                💰 See All Transparent Pricing
              </a>
            </div>

            {/* BirdEye Widget */}
            <div className="mb-8 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 max-w-2xl mx-auto">
              <div id="bf-revz-widget-9876543218815183" aria-label="Customer Reviews Widget"></div>
              <div id="birdeye-fallback" style={{ display: 'none' }} className="text-trust-light">
                <p>Reviews loading... See our 1000+ verified reviews across Google, BirdEye, and more.</p>
              </div>
            </div>
          </div>
        </section>

        {/* 5-Card Trust Section */}
        <section className="py-12 bg-gradient-to-bl from-trust-deep to-authority-navy">
          <div className="container-custom">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-yellow-400 mb-2">18,000+</div>
                <div className="text-lg font-semibold text-white mb-2">Jobs Completed</div>
                <p className="text-blue-100 text-sm">Since 2018 serving Denver Metro</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-green-400 mb-2">1000+</div>
                <div className="text-lg font-semibold text-white mb-2">Reviews Online</div>
                <p className="text-blue-100 text-sm">4.8★ average across Google (313+), BirdEye (300+), HomeAdvisor (92+), and other online sources</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-blue-400 mb-2">30+</div>
                <div className="text-lg font-semibold text-white mb-2">CO Jurisdictions</div>
                <p className="text-blue-100 text-sm">Licensed in 30+ Colorado cities including Denver, with all permits and inspections handled</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-green-400 mb-2">A+</div>
                <div className="text-lg font-semibold text-white mb-2">BBB Rating</div>
                <p className="text-blue-100 text-sm">Accredited 7/15/2024</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-orange-400 mb-2">ONLY</div>
                <div className="text-lg font-semibold text-white mb-2">Licensed Plumber</div>
                <p className="text-blue-100 text-sm">Showing Affordable Prices Online with 1000+ Verified Reviews Across All Platforms</p>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-white">
              Affordable Professional Plumbing Services in Denver
            </h2>
            <p className="text-center text-blue-100 mb-12 max-w-3xl mx-auto">
              From <Link to="/residential-plumbing" className="text-blue-200 hover:text-blue-100 underline">emergency repairs</Link> to complete <Link to="/bathroom-kitchen-remodel" className="text-blue-200 hover:text-blue-100 underline">home remodels</Link>, we provide <Link to="/pricing" className="text-blue-200 hover:text-blue-100 underline">affordable transparent pricing</Link> for every service
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <Link to="/water-heater-pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Droplet className="w-12 h-12 text-blue-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Water Heater Replacement</h3>
                <p className="text-blue-100">Rheem and AO Smith atmospheric. 40-gallon $2,000, 50-gallon $2,100!</p>
              </Link>
              
              <Link to="/tankless-water-heater-pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Zap className="w-12 h-12 text-yellow-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Tankless Water Heater</h3>
                <p className="text-blue-100">Navien, Rinnai tankless. Replace $5,000, New Install $8,000!</p>
              </Link>
              
              <Link to="/sewer-repair-replacement" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Wrench className="w-12 h-12 text-green-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Sewer Repair & Replacement</h3>
                <p className="text-blue-100">Spot repair $5,000/$9,500, Full replacement $12,000!</p>
              </Link>
              
              <Link to="/sewer-cleaning-pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Camera className="w-12 h-12 text-purple-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Sewer Cleaning & Scope</h3>
                <p className="text-blue-100">Camera scope $175, Snake $295, Hydro-jet $695!</p>
              </Link>
              
              <Link to="/bathroom-kitchen-remodel" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Home className="w-12 h-12 text-orange-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Remodel & Renovation</h3>
                <p className="text-blue-100">Complete bathroom and kitchen plumbing renovations!</p>
              </Link>
              
              <Link to="/commercial-plumbing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Building className="w-12 h-12 text-red-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Commercial Plumbing</h3>
                <p className="text-blue-100">Downtown Denver building expertise & coordination!</p>
              </Link>
              
              <Link to="/pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Shield className="w-12 h-12 text-indigo-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Much More</h3>
                <p className="text-blue-100">See all services with transparent, upfront pricing!</p>
              </Link>
              
              <Link to="/diagnostic-services-pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Gauge className="w-12 h-12 text-cyan-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Diagnostic Services</h3>
                <p className="text-blue-100">$95 standard, $175 sewer scope, $225 difficult diagnosis!</p>
              </Link>
            </div>
          </div>
        </section>

        {/* NEW: How Can Others Claim "Affordable" Section */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
              How Can Others Claim "Affordable" Without Showing Prices?
            </h2>
            <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-xl mb-6 text-white font-semibold">
                We are the only Licensed Plumbing Contractor in Colorado proving affordable means transparent. True affordable plumbing shows <Link to="/water-heater-pricing" className="text-yellow-300 hover:text-yellow-200 underline">water heater costs</Link>, <Link to="/tankless-water-heater-pricing" className="text-yellow-300 hover:text-yellow-200 underline">tankless prices</Link>, <Link to="/sewer-repair-replacement" className="text-yellow-300 hover:text-yellow-200 underline">sewer repair rates</Link>, and more - all online before you call.
              </p>
              <p className="text-lg mb-6 text-blue-100">
                Affordable doesn't mean hidden fees or surprise charges. It means eliminating sales commissions, showing exact prices, and delivering professional service at fair rates. No salespeople, no upsells, just honest plumbing from Denver's most trusted team.
              </p>
              
              {/* Price Comparison Cards */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-6">
                  <h3 className="text-green-400 font-bold mb-4">✅ Our Affordable Price: $9,500 (Street Sewer Repair)</h3>
                  <ul className="text-blue-100 space-y-2">
                    <li>✓ Exact price online</li>
                    <li>✓ Includes all permits</li>
                    <li>✓ Street restoration included</li>
                    <li>✓ Written guarantee</li>
                  </ul>
                </div>
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6">
                  <h3 className="text-red-400 font-bold mb-4">❌ Others' "Affordable": $???</h3>
                  <ul className="text-blue-100 space-y-2">
                    <li>✗ "Free estimate" required</li>
                    <li>✗ High-pressure sales tactics</li>
                    <li>✗ Price varies by salesperson</li>
                    <li>✗ Hidden fees added later</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ 1: Sewer Repair in Denver Street */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white">
              How Much Does a Sewer Repair in a Denver Street Cost?
            </h2>
            
            <div className="max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 mb-8 border border-white/20">
                <img 
                  src="/page-specific-content/denver-plumber/sewer-spot-repair-denver.JPEG" 
                  alt="Professional sewer repair in Denver street by Colorado First Plumbing with traffic control and city permit compliance"
                  className="w-full rounded-lg mb-6 shadow-lg"
                  loading="lazy"
                />
                
                <p className="text-lg mb-6 text-blue-100">
                  <strong className="text-yellow-300">Colorado First Plumbing and Sewer LLC provides flat-rate pricing of $9,500 for sewer repairs requiring excavation into public streets in Denver.</strong> This all-inclusive price accounts for the significant complexities of working within the City of Denver's right-of-way, ensuring a compliant and complete repair with no surprise fees. Our affordable pricing proves transparency means no hidden costs.
                </p>
                
                <h3 className="text-xl font-bold mb-4 text-white">What's Covered in Your $9,500 Denver Street Repair Price?</h3>
                <div className="bg-white/5 rounded-lg p-6 mb-6">
                  <p className="text-blue-100 mb-4">
                    This comprehensive price includes securing all necessary permits from Denver Public Works, managing traffic control per city requirements, asphalt or concrete cutting, professional excavation and shoring, the pipe repair itself, and full restoration of the street surface to meet Denver's stringent standards. We handle the entire process, including navigating the complexities of buried utilities common in dense urban areas like Downtown, LoDo, and RiNo.
                  </p>
                </div>
                
                <h3 className="text-xl font-bold mb-4 text-white">Why Are Street Repairs More Involved in Denver?</h3>
                <p className="text-blue-100 mb-4">
                  Working under a city street involves navigating a web of other utilities (gas, water, fiber optic) and requires a higher level of coordination and compliance. Denver's Public Works Department has specific requirements for street cuts, restoration standards, and traffic control measures. Our expertise ensures the project is completed safely, efficiently, and correctly, restoring both your sewer function and the public infrastructure without any hassle for you.
                </p>
                
                <h3 className="text-xl font-bold mb-4 text-white">A Targeted Solution for Under-Street Sewer Failures</h3>
                <p className="text-blue-100">
                  If a <Link to="/diagnostic-services-pricing" className="text-yellow-300 hover:text-yellow-200 underline">camera inspection ($175)</Link> reveals that the sole problem with your sewer line—such as a collapsed section or a failed tap connection—is located under the street, this $9,500 spot repair is the most precise and cost-effective solution. It's significantly more affordable than full replacement while addressing the exact problem area.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ 2: Full Sewer Replacement */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white">
              What is the Affordable Price to Replace an Old Sewer Line in Denver?
            </h2>
            
            <div className="max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 mb-8 border border-white/20">
                <img 
                  src="/page-specific-content/denver-plumber/replace-full-sewer-main.jpeg" 
                  alt="Trenchless sewer line replacement in Denver preserving mature landscaping in historic neighborhoods"
                  className="w-full rounded-lg mb-6 shadow-lg"
                  loading="lazy"
                />
                
                <p className="text-lg mb-6 text-blue-100">
                  <strong className="text-yellow-300">Colorado First Plumbing offers affordable full sewer line replacement in Denver for a flat rate of $12,000.</strong> The City and County of Denver has specific requirements for sewer replacement methods. Traditional excavation is typically required where feasible, ensuring compliance with Denver's precise municipal standards. Our transparent pricing proves affordable doesn't mean surprise charges or hidden fees.
                </p>
                
                <h3 className="text-xl font-bold mb-4 text-white">Understanding Our $12,000 Denver Sewer Replacement Service</h3>
                <div className="bg-white/5 rounded-lg p-6 mb-6">
                  <p className="text-blue-100 mb-4">
                    This all-inclusive price covers the entire replacement process from your home's foundation to the city main connection. It includes a new, seamless HDPE pipe that is root-proof and rated to last over 50 years, professional excavation following Denver's municipal requirements, and all required city permits and inspections. Traditional excavation is Denver's preferred method, but trenchless pipe bursting may be approved for special circumstances like detached garages or property access constraints.
                  </p>
                </div>
                
                <h3 className="text-xl font-bold mb-4 text-white">Denver's Requirements for Alternative Installation Methods</h3>
                <p className="text-blue-100 mb-4">
                  When traditional excavation would cause significant property damage - such as homes with detached garages, extensive landscaping, or access constraints - Denver may approve trenchless pipe bursting methods. This requires submitting project details for city approval before work begins. Our experienced team handles all coordination with Denver's Public Works Department to determine the appropriate method for your specific property and ensures full compliance with municipal requirements.
                </p>
                
                <h3 className="text-xl font-bold mb-4 text-white">A Clear, Affordable Price for Both Denver-Approved Methods</h3>
                <p className="text-blue-100">
                  We provide this exact $12,000 price online for both traditional excavation and Denver-approved trenchless methods, so you can plan for this critical home infrastructure upgrade with full confidence. The method depends on Denver's requirements for your specific property, but our transparent pricing remains the same. Unlike other companies claiming "affordable" without showing prices, we prove transparency is truly affordable with no surprise charges regardless of the approved installation method.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ 3: Sewer Diagnosis Process */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white">
              How Do You Determine the Right Sewer Fix for My Denver Home?
            </h2>
            
            <div className="max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 mb-8 border border-white/20">
                <img 
                  src="/page-specific-content/denver-plumber/sewer-locate-affordable-denver.JPEG" 
                  alt="Professional sewer camera inspection and locate service determining exact problem location in Denver home"
                  className="w-full rounded-lg mb-6 shadow-lg"
                  loading="lazy"
                />
                
                <p className="text-lg mb-6 text-blue-100">
                  <strong className="text-yellow-300">Colorado First Plumbing and Sewer LLC starts every diagnosis with a professional sewer camera inspection for a flat rate of $175.</strong> This allows us to visually confirm the exact nature and location of the problem inside your pipe, ensuring we recommend the most effective and fairly priced solution for your specific Denver home. Our affordable transparent approach means you see exactly what we see.
                </p>
                
                <h3 className="text-xl font-bold mb-4 text-white">Your $175 Inspection: The Key to an Accurate, Affordable Solution</h3>
                <div className="bg-white/5 rounded-lg p-6 mb-6">
                  <p className="text-blue-100 mb-4">
                    From a simple clog in a new build to a collapsed line in a century-old Denver bungalow, our camera inspection provides a clear roadmap. We'll give you a video recording of the findings, so you can see the issue yourself. This step ensures you won't pay for a service you don't need, proving affordable means honest diagnosis. Note: Complex access, like roof vents or toilet removal, may have additional fees.
                  </p>
                </div>
                
                <h3 className="text-xl font-bold mb-4 text-white">A Menu of Transparent, Flat-Rate Solutions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                    <p className="text-yellow-300 font-bold">Based on camera findings:</p>
                    <ul className="text-blue-100 space-y-2 mt-2">
                      <li>• <strong>Mainline Snake (from cleanout):</strong> $295 for common blockages</li>
                      <li>• <strong>Hydro-Jetting:</strong> $695 to scour pipes clean of grease or roots</li>
                      <li>• <strong>Root Removal:</strong> $495-$795 for aggressive root problems</li>
                    </ul>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                    <p className="text-yellow-300 font-bold">Major repairs if needed:</p>
                    <ul className="text-blue-100 space-y-2 mt-2">
                      <li>• <strong>Sewer Spot Repair (Yard):</strong> $5,000 for localized pipe failure</li>
                      <li>• <strong>Sewer Spot Repair (Street):</strong> $9,500 with permits included</li>
                      <li>• <strong>Full Replacement:</strong> $12,000 for lines beyond repair</li>
                    </ul>
                  </div>
                </div>
                
                <h3 className="text-xl font-bold mb-4 text-white">Empowering Denver Homeowners with Factual Information</h3>
                <p className="text-blue-100">
                  Our process is designed to replace uncertainty with clarity. You get a professional diagnosis and an exact price, allowing you to make a smart, informed decision about your property. This is what affordable truly means - no sales pressure, no surprise charges, just honest evaluation and transparent pricing from Denver's most trusted <Link to="/team/codee-jessen" className="text-yellow-300 hover:text-yellow-200 underline">Master Plumber</Link>.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ 4: Tankless Water Heater */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white">
              What is the Cost for Tankless Water Heater Installation in Denver?
            </h2>
            
            <div className="max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 mb-8 border border-white/20">
                <img 
                  src="/page-specific-content/denver-plumber/best-denver-tankless-install-rinnai.JPEG" 
                  alt="Professional Rinnai tankless water heater installation in Denver home with proper venting for altitude"
                  className="w-full rounded-lg mb-6 shadow-lg"
                  loading="lazy"
                />
                
                <p className="text-lg mb-6 text-blue-100">
                  <strong className="text-yellow-300">Colorado First Plumbing provides affordable tankless water heater installation in Denver for $5,000 to replace existing units or $8,000 for new installations converting from traditional tanks.</strong> This service is perfect for Denver homeowners who already have a modern Rinnai or Navien system and want to upgrade to the newest, most efficient model, or those ready to make the switch to endless hot water.
                </p>
                
                <h3 className="text-xl font-bold mb-4 text-white">What's Included in the $5,000 Tankless Replacement?</h3>
                <div className="bg-white/5 rounded-lg p-6 mb-6">
                  <p className="text-blue-100 mb-4">
                    This affordable price covers a new, high-efficiency Rinnai or Navien unit, professional labor to swap out the old heater, minor modifications to water, gas, and vent lines (up to 5'), and proper programming and testing for Denver's altitude (5,280 feet affects combustion settings). This service assumes your existing venting and gas lines are correct for the new unit and the original install was permitted - proving affordable means doing it right.
                  </p>
                </div>
                
                <h3 className="text-xl font-bold mb-4 text-white">Denver-Specific Considerations for Tankless</h3>
                <p className="text-blue-100 mb-4">
                  Denver's cold groundwater temperature (40°F in winter) and high altitude require proper sizing and adjustment of tankless units. Our experienced team ensures your unit is correctly calibrated for optimal performance in Denver's unique environment. Whether you're in a newer development in Green Valley Ranch or renovating a historic home in Congress Park, we provide the expertise needed for reliable endless hot water.
                </p>
                
                <h3 className="text-xl font-bold mb-4 text-white">New Installation ($8,000) - Converting from Tank to Tankless</h3>
                <p className="text-blue-100">
                  For homeowners ready to upgrade from traditional tank water heaters, our $8,000 new installation includes everything needed: the tankless unit, new venting system, gas line upgrade if required, electrical outlet installation, removal of old tank, and all permits. This transparent, affordable pricing lets you plan your upgrade without surprise costs or hidden fees - something other "affordable" plumbers can't claim.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ 5: 50 Gallon Water Heater */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white">
              What's the Affordable Cost for 50-Gallon Water Heater Installation in Denver?
            </h2>
            
            <div className="max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 mb-8 border border-white/20">
                <img 
                  src="/page-specific-content/denver-plumber/rheem-50-gallon-installed-2000 (1).JPEG" 
                  alt="Rheem 50-gallon water heater professionally installed in Denver home with thermal expansion tank and drain pan"
                  className="w-full rounded-lg mb-6 shadow-lg"
                  loading="lazy"
                />
                
                <p className="text-lg mb-6 text-blue-100">
                  <strong className="text-yellow-300">Colorado First Plumbing and Sewer LLC offers affordable 50-gallon water heater installation in Denver for $2,100 during our Summer Special (regular price $2,525).</strong> For premium Bradford White models, the flat-rate price is $2,525. This all-inclusive pricing proves that affordable means transparent - the price you see is the final price you pay, with no hidden fees or surprise charges.
                </p>
                
                <h3 className="text-xl font-bold mb-4 text-white">Your Complete "Out-the-Door" Price</h3>
                <div className="bg-white/5 rounded-lg p-6 mb-6">
                  <p className="text-blue-100 mb-4">
                    "All-inclusive" means exactly that. Our $2,100 price covers the new 50-gallon Rheem or AO Smith unit, professional installation that meets all Denver plumbing codes, a new thermal expansion tank (required by code), a drain pan with proper drainage, new supply lines, hauling away your old unit, and securing the city permit. No surprises, no add-ons, no "affordable" claims without proof.
                  </p>
                </div>
                
                <h3 className="text-xl font-bold mb-4 text-white">The Right Solution for Denver Families</h3>
                <p className="text-blue-100 mb-4">
                  A 50-gallon unit is ideal for larger Denver homes or families with high hot water demand, perfect for properties in Cherry Creek with multiple bathrooms, or busy households in Stapleton with teenagers. Denver's hard water (10-12 grains hardness from our mountain snowmelt) can affect water heater lifespan, but our professional installation includes proper setup to maximize your unit's life.
                </p>
                
                <h3 className="text-xl font-bold mb-4 text-white">Why Our Transparent Pricing Matters</h3>
                <p className="text-blue-100">
                  While other companies advertise "affordable water heater installation" without showing prices, we prove affordable means honest. Compare our exact $2,100 price to competitors who only reveal costs after sending a salesperson to your home. With <Link to="/water-heater-pricing" className="text-yellow-300 hover:text-yellow-200 underline">all our water heater models and prices online</Link>, you can make an informed decision without pressure or games.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ 6: Professional Sewer Cleaning */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white">
              What Does Mainline Snaking Cost in Older Denver Homes Without Cleanouts?
            </h2>
            
            <div className="max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 mb-8 border border-white/20">
                <img 
                  src="/page-specific-content/denver-plumber/root-cleaning-descale-machine-denver.jpg" 
                  alt="Professional root cleaning and descaling equipment used for Denver sewer maintenance"
                  className="w-full rounded-lg mb-6 shadow-lg"
                  loading="lazy"
                />
                
                <p className="text-lg mb-6 text-blue-100">
                  <strong className="text-yellow-300">Colorado First Plumbing charges a flat rate of $695 for mainline snaking that includes pulling and resetting a toilet in older Denver homes lacking exterior cleanouts.</strong> This all-in-one price covers the entire service, providing access and clearing the clog - proving affordable plumbing includes being upfront about every cost.
                </p>
                
                <h3 className="text-xl font-bold mb-4 text-white">Our $695 All-Inclusive Toilet-Pull Snaking Service</h3>
                <div className="bg-white/5 rounded-lg p-6 mb-6">
                  <p className="text-blue-100 mb-4">
                    This comprehensive service is a complete solution for homes without convenient access points. It includes the careful removal of your toilet, professional snaking of the main sewer line to clear the blockage, and the proper re-installation of the toilet with a brand-new wax ring and bolts to ensure a leak-proof seal. Many homes in neighborhoods like Berkeley, Platt Park, and Congress Park were built before exterior sewer cleanouts became standard.
                  </p>
                </div>
                
                <h3 className="text-xl font-bold mb-4 text-white">Common in Denver's Historic Neighborhoods</h3>
                <p className="text-blue-100 mb-4">
                  Denver's charming older neighborhoods - including Capitol Hill's Victorian homes, the Highlands' turn-of-the-century bungalows, and Washington Park's classic Denver Squares - often lack modern cleanout access. This specialized service addresses that common scenario with transparent, affordable pricing. No hidden "access fees" or surprise charges like other companies claiming to be affordable.
                </p>
                
                <h3 className="text-xl font-bold mb-4 text-white">Alternative Professional Cleaning Options</h3>
                <div className="bg-white/5 rounded-lg p-4">
                  <p className="text-blue-100 mb-4">
                    For different situations, we offer a complete menu of <Link to="/sewer-cleaning-pricing" className="text-yellow-300 hover:text-yellow-200 underline">professional sewer cleaning services</Link> with transparent pricing:
                  </p>
                  <ul className="text-blue-100 space-y-2">
                    <li>• <strong>Mainline Snake (exterior cleanout):</strong> $295 for easy access</li>
                    <li>• <strong>Hydro-Jetting:</strong> $695 for thorough high-pressure cleaning</li>
                    <li>• <strong>Root Removal:</strong> $495-$795 for aggressive tree root problems</li>
                    <li>• <strong>Camera Inspection:</strong> $175 to see exactly what's causing the issue</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Master Plumber Attribution Box */}
        <section className="py-12 bg-gradient-to-br from-blue-800 to-blue-900">
          <div className="container-custom max-w-4xl">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex-shrink-0">
                  <img 
                    src="/colorado-plumbing-uploads/brand/codee-christian-plumbing-truck.png" 
                    alt="Codee Jessen, Licensed Master Plumber, with Colorado First Plumbing and Sewer LLC truck serving Denver Metro"
                    className="w-32 h-32 rounded-full object-contain bg-white/5 border-4 border-white/20 shadow-lg"
                    loading="lazy"
                  />
                </div>
                <div className="flex-grow text-center md:text-left">
                  <h3 className="text-2xl font-bold text-white mb-2">Written by <Link to="/team/codee-jessen" className="text-blue-200 hover:text-blue-100 underline">Codee Jessen</Link>, Licensed Master Plumber</h3>
                  <div className="text-blue-100 mb-4">
                    <p className="mb-2">• Colorado Master Plumber License</p>
                    <p className="mb-2">• 18+ Years Experience</p>
                    <p className="mb-2">• 18,000+ Jobs Completed</p>
                    <p className="mb-2">• Specialties: Sewer Replacement, Tankless Installation, Commercial Projects</p>
                  </div>
                  <p className="text-sm text-blue-200 italic">
                    "Affordable doesn't mean cutting corners - it means efficient, professional service with transparent pricing. Proud to serve the Denver Metro community with the same integrity we've built our reputation on."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Common Issues Section - Denver Specific */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950">
          <div className="container-custom">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
              Common Plumbing Issues in Denver Neighborhoods
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-center mb-4">
                  <AlertTriangle className="w-8 h-8 text-yellow-400 mr-3" />
                  <h3 className="text-xl font-bold text-white">Clay Pipe Problems (Historic Denver)</h3>
                </div>
                <p className="text-blue-100 mb-4">
                  Many homes in older Denver neighborhoods like Capitol Hill, Wash Park, and the Highlands have original clay sewer lines from the 1920s-1950s. These pipes are brittle, prone to root intrusion, and often have separated joints.
                </p>
                <p className="text-sm text-yellow-300">
                  Solution: Our $175 camera inspection identifies problems early. Spot repairs cost $5,000 (yard) or $9,500 (street), full replacement $12,000.
                </p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-center mb-4">
                  <Droplet className="w-8 h-8 text-blue-400 mr-3" />
                  <h3 className="text-xl font-bold text-white">Hard Water Issues (Denver Metro)</h3>
                </div>
                <p className="text-blue-100 mb-4">
                  Denver's water averages 10-12 grains of hardness from mineral content in our mountain water sources. This causes scale buildup in water heaters, reducing efficiency and lifespan.
                </p>
                <p className="text-sm text-yellow-300">
                  Solution: Professional water heater installation with proper setup. 40-gallon $2,000, 50-gallon $2,100, tankless $5,000 replacement.
                </p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-center mb-4">
                  <Building className="w-8 h-8 text-orange-400 mr-3" />
                  <h3 className="text-xl font-bold text-white">Downtown Building Challenges</h3>
                </div>
                <p className="text-blue-100 mb-4">
                  Commercial buildings and high-rises in Downtown, LoDo, and RiNo require specialized coordination with building management, after-hours work, and compliance with strict city codes.
                </p>
                <p className="text-sm text-yellow-300">
                  Solution: Our commercial team handles building management coordination. Same transparent pricing, professional service for Denver businesses.
                </p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-center mb-4">
                  <Home className="w-8 h-8 text-green-400 mr-3" />
                  <h3 className="text-xl font-bold text-white">No Cleanout Access (Older Homes)</h3>
                </div>
                <p className="text-blue-100 mb-4">
                  Homes built before 1960 in neighborhoods like Berkeley, Platt Park, and Congress Park often lack exterior sewer cleanouts, requiring toilet removal for mainline access.
                </p>
                <p className="text-sm text-yellow-300">
                  Solution: Toilet-pull mainline snaking for $695 includes removal, snaking, and reinstallation with new seal. Complete transparent service.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900">
          <div className="container-custom">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20 max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                Ready for Truly Affordable Plumbing Service in Denver?
              </h2>
              <p className="text-xl mb-8 text-blue-100 max-w-3xl mx-auto">
                Stop wondering what "affordable" means. See exact prices online, no hidden fees, no surprises. Denver's only transparent plumber with 1000+ verified reviews across all platforms.
              </p>
              
              {/* Price Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto mb-12">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <div className="text-3xl font-bold text-orange-400 mb-2">$5,000</div>
                  <p className="text-white font-semibold">Tankless Replace</p>
                  <p className="text-sm text-blue-100">Affordable existing unit replacement</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <div className="text-3xl font-bold text-green-400 mb-2">$2,000</div>
                  <p className="text-white font-semibold">40 Gal Water Heater</p>
                  <p className="text-sm text-blue-100">Summer special pricing</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <div className="text-3xl font-bold text-blue-400 mb-2">$12,000</div>
                  <p className="text-white font-semibold">Full Sewer Replace</p>
                  <p className="text-sm text-blue-100">Trenchless technology</p>
                </div>
              </div>
              
              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                
                <Link
                  to="/pricing" 
                  className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-8 py-4 rounded-full text-xl font-semibold inline-block transition-all duration-300"
                >
                  💰 View All Transparent Pricing
                </Link>
              </div>
              
              <p className="text-sm text-blue-200 mt-6">
                Serving Denver Metro since 2018 • Licensed Master Plumber • Woman Majority Owned • BBB A+ Rating
              </p>
            </div>
          </div>
        </section>

        {/* Service Area Section */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950">
          <div className="container-custom">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
              Serving All Denver Neighborhoods
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20 text-center">
                <Building className="w-8 h-8 text-orange-400 mx-auto mb-2" />
                <h3 className="font-bold text-white mb-2">Downtown Denver</h3>
                <p className="text-sm text-blue-100">LoDo, RiNo, Union Station</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20 text-center">
                <Home className="w-8 h-8 text-green-400 mx-auto mb-2" />
                <h3 className="font-bold text-white mb-2">Central Denver</h3>
                <p className="text-sm text-blue-100">Capitol Hill, Cherry Creek</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20 text-center">
                <MapPin className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                <h3 className="font-bold text-white mb-2">North Denver</h3>
                <p className="text-sm text-blue-100">Highlands, Berkeley, Regis</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20 text-center">
                <Users className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                <h3 className="font-bold text-white mb-2">South Denver</h3>
                <p className="text-sm text-blue-100">Wash Park, Platt Park</p>
              </div>
            </div>
            
            <p className="text-center text-blue-100 mt-8">
              Complete coverage for all Denver zip codes: 80202, 80203, 80204, 80205, 80206, 80207, 80209, 80210, 80211, 80218, 80220 and surrounding areas
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default DenverPlumber;